import { Box, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { IconButton } from '@/components/form/baseInputs/Button/IconButton';
import { Switch } from '@/components/form/baseInputs/Switch';
import { TextInput } from '@/components/form/baseInputs/TextInput';
import { CheckCircleIcon } from '@/components/icons/CheckCircleIcon';
import { EyeIcon } from '@/components/icons/EyeIcon';
import { EyeOffIcon } from '@/components/icons/EyeOffIcon';
import { Card } from '@/components/layout/Card/Card';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useReportError } from '@/hooks/useReportError';
import {
  OpenAiIntegrationCard_OpenaiSdkConfigFragment,
  useCreateOpenaiSdkConfigMutation,
  useUpdateOpenaiSdkConfigMutation,
} from '@/pages/admin/AdminIntegrationsPage/graphql/AdminIntegrationsPage.generated';
import { COLORS } from '@/styles/tokens/colors';
import { OpenaiSdkConfigProvider } from '@/types/schema';
import { getPublicImageUrl } from '@/utils/staticFiles';

export interface OpenAIIntegrationCardProps {
  openaiConfig: OpenAiIntegrationCard_OpenaiSdkConfigFragment | null;
  otherProviderEnabled: boolean;
  disabled?: boolean;
}

export function OpenAIIntegrationCard({
  openaiConfig,
  otherProviderEnabled,
  disabled = false,
}: OpenAIIntegrationCardProps) {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();

  const [createOpenaiSdkConfig, { loading: createLoading }] =
    useCreateOpenaiSdkConfigMutation({
      onError: (err) => {
        showFeedback('Failed to create OpenAI configuration');
        reportError('Failed to create OpenAI configuration', err);
      },
    });

  const [updateOpenaiSdkConfig, { loading: updateLoading }] =
    useUpdateOpenaiSdkConfigMutation({
      onError: (err) => {
        showFeedback('Failed to update OpenAI API key');
        reportError('Failed to update OpenAI API key', err);
      },
    });

  const loading = createLoading || updateLoading;

  const DEFAULT_BASE_URL = 'https://api.openai.com';

  const isEnabled = openaiConfig?.enabled ?? false;
  const [customKey, setCustomKey] = useState('');
  const [showCustomKey, setShowCustomKey] = useState(false);
  const [baseURL, setBaseURL] = useState(DEFAULT_BASE_URL);
  const isBaseURLValid = (() => {
    if (!baseURL) return true;
    try {
      new URL(baseURL);
      return true;
    } catch {
      return false;
    }
  })();

  useEffect(() => {
    if (openaiConfig?.baseURL) {
      setBaseURL(openaiConfig.baseURL);
    }
  }, [openaiConfig?.baseURL]);

  const handleToggleEnabled = useCallback(
    async (enabled: boolean) => {
      if (!openaiConfig) return;

      await updateOpenaiSdkConfig({
        variables: {
          input: {
            id: openaiConfig.id,
            update: {
              enabled,
            },
          },
        },
        refetchQueries: ['GetIntegrationConfiguration'],
        awaitRefetchQueries: true,
        onCompleted: () => {
          showFeedback(
            enabled ? 'OpenAI provider enabled' : 'OpenAI provider disabled',
            { variant: 'success', autoHideDuration: 3000 }
          );
        },
      });
    },
    [openaiConfig, updateOpenaiSdkConfig, showFeedback]
  );

  const handleSaveCustomKey = useCallback(async () => {
    if ((!openaiConfig && !customKey) || !isBaseURLValid) return;

    setShowCustomKey(false);

    if (openaiConfig) {
      // Update existing config and enable it
      await updateOpenaiSdkConfig({
        variables: {
          input: {
            id: openaiConfig.id,
            update: {
              apiKey: customKey || undefined,
              baseURL,
              enabled: true,
            },
          },
        },
        refetchQueries: ['GetIntegrationConfiguration'],
        awaitRefetchQueries: true,
        onCompleted: () => {
          setCustomKey('');
          showFeedback('Successfully set custom OpenAI API key', {
            variant: 'success',
            autoHideDuration: 3000,
          });
        },
      });
    } else {
      // Create new config with enabled: true
      await createOpenaiSdkConfig({
        variables: {
          input: {
            create: {
              provider: OpenaiSdkConfigProvider.Openai,
              apiKey: customKey,
              baseURL,
              enabled: true,
            },
          },
        },
        refetchQueries: ['GetIntegrationConfiguration'],
        awaitRefetchQueries: true,
        onCompleted: () => {
          setCustomKey('');
          showFeedback('Successfully set custom OpenAI API key', {
            variant: 'success',
            autoHideDuration: 3000,
          });
        },
      });
    }
  }, [
    openaiConfig,
    showFeedback,
    updateOpenaiSdkConfig,
    createOpenaiSdkConfig,
    customKey,
    baseURL,
    isBaseURLValid,
  ]);

  const hasConfig = !!openaiConfig;

  // Toggle disabled when: other provider enabled OR no config exists
  const toggleDisabled =
    disabled || loading || (otherProviderEnabled && !isEnabled) || !hasConfig;

  // Form visible when: enabled OR neither provider is enabled (initial setup)
  const showForm = isEnabled || !otherProviderEnabled;

  return (
    <Stack>
      <Box textAlign="center">
        <img
          alt="OpenAI"
          src={getPublicImageUrl('/openAILogo.png')}
          height={40}
        />
      </Box>
      <Stack mt={1} spacing={2}>
        <Typography variant="body-s">
          Set your own OpenAI API key. This will be used for all AI-related
          tasks on the Luminary Platform. Or leave this empty to use
          Luminary&#39;s default API key.
        </Typography>
        {isEnabled && (
          <Stack direction="row" alignItems="center" gap={1.5}>
            <CheckCircleIcon color={COLORS.FUNCTIONAL.SUCCESS.DEFAULT} />
            <Typography variant="title-xs">
              Currently using custom OpenAI API key
            </Typography>
          </Stack>
        )}
        <Card
          variant="filled"
          sx={{ display: 'flex', flexDirection: 'column', p: 2, gap: 2 }}
        >
          <Switch
            labelPosition="right"
            value={isEnabled}
            label="Use custom OpenAI Credentials"
            disabled={toggleDisabled}
            onChange={(e) => handleToggleEnabled(e.target.checked)}
          />
          {showForm && (
            <>
              <TextInput
                fullWidth
                label="Base URL"
                value={baseURL}
                onChange={(e) => setBaseURL(e.target.value)}
                disabled={disabled || loading}
                placeholder={DEFAULT_BASE_URL}
                errorMessage={
                  isBaseURLValid ? undefined : 'Please enter a valid URL'
                }
              />
              <TextInput
                fullWidth
                label="OpenAI API key"
                type={showCustomKey ? 'text' : 'password'}
                value={customKey}
                onChange={(e) => setCustomKey(e.target.value)}
                disabled={disabled || loading}
                helpText={
                  openaiConfig
                    ? 'An API key is already configured. Enter a new key to update.'
                    : undefined
                }
                InputProps={{
                  componentsProps: { root: { style: { paddingRight: 8 } } },
                }}
                endAdornment={
                  <IconButton
                    variant="transparent"
                    size="sm"
                    icon={showCustomKey ? EyeOffIcon : EyeIcon}
                    disabled={disabled || !customKey || loading}
                    onClick={() => setShowCustomKey((prev) => !prev)}
                    ariaLabel="Toggle visibility"
                    sx={{ borderRadius: '50%', p: 1 }}
                  />
                }
              />
            </>
          )}
        </Card>
      </Stack>
      {showForm && (
        <Box mt={2} mb={2} textAlign="center">
          <Button
            size="sm"
            variant="primary"
            fullWidth
            disabled={
              disabled || (!openaiConfig && !customKey) || !isBaseURLValid
            }
            loading={loading}
            onClick={handleSaveCustomKey}
          >
            {isEnabled ? 'Update' : 'Enable'}
          </Button>
        </Box>
      )}
    </Stack>
  );
}
